import { useState, useMemo } from 'react';
import { Bell, BellOff, BellRing, Settings, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { 
    requestNotificationPermission, 
    isPushSupported, 
    getNotificationPrefs, 
    saveNotificationPrefs,
    getReminderIntervals,
    sendTestNotification 
} from '../../utils/notifications';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';

const NotificationToggle = ({ onPermissionChange, onPrefsChange }) => {
    // Use useMemo to compute initial values once - avoids setState in useEffect
    const isSupported = useMemo(() => isPushSupported(), []);
    const initialPermission = useMemo(() => {
        if (typeof window !== 'undefined' && 'Notification' in window) {
            return Notification.permission;
        }
        return 'default';
    }, []);
    
    const [permission, setPermission] = useState(initialPermission);
    const [isExpanded, setIsExpanded] = useState(false);
    const [prefs, setPrefs] = useState(getNotificationPrefs());
    const [isSendingTest, setIsSendingTest] = useState(false);

    const handleEnable = async () => {
        const newPermission = await requestNotificationPermission();
        setPermission(newPermission);

        if (newPermission === 'granted') {
            const newPrefs = { ...prefs, enabled: true };
            setPrefs(newPrefs);
            saveNotificationPrefs(newPrefs);
            onPrefsChange?.(newPrefs);
            toast.success('Notifications enabled!', {
                description: "You'll receive deadline reminders"
            });
        } else if (newPermission === 'denied') {
            toast.error('Notifications blocked', {
                description: 'Please enable in browser settings'
            });
        }
        
        onPermissionChange?.(newPermission);
    };

    const handleToggleEnabled = (enabled) => {
        const newPrefs = { ...prefs, enabled };
        setPrefs(newPrefs);
        saveNotificationPrefs(newPrefs);
        onPrefsChange?.(newPrefs);
        
        if (enabled) {
            toast.success('Notifications enabled');
        } else {
            toast.info('Notifications paused');
        }
    };

    const handleIntervalToggle = (days, enabled) => {
        const newPrefs = {
            ...prefs,
            intervals: {
                ...prefs.intervals,
                [days]: enabled
            }
        };
        setPrefs(newPrefs);
        saveNotificationPrefs(newPrefs);
        onPrefsChange?.(newPrefs);
    };

    const handleCriticalOnlyToggle = (criticalOnly) => {
        const newPrefs = { ...prefs, criticalOnly };
        setPrefs(newPrefs);
        saveNotificationPrefs(newPrefs);
        onPrefsChange?.(newPrefs);
    };

    const handleTestNotification = async () => {
        setIsSendingTest(true);
        const success = await sendTestNotification();
        setIsSendingTest(false);
        
        if (success) {
            toast.success('Test notification sent!');
        } else {
            toast.error('Failed to send test notification');
        }
    };

    if (!isSupported) {
        return (
            <div className="p-4 bg-slate-50 rounded-2xl text-center">
                <BellOff className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Notifications not supported in this browser</p>
            </div>
        );
    }

    // Not yet granted permission
    if (permission !== 'granted') {
        return (
            <button
                onClick={handleEnable}
                data-testid="enable-notifications-btn"
                className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all ${
                    permission === 'denied'
                        ? 'bg-red-50 text-red-700'
                        : 'bg-[#1C6EE7]/10 text-[#1C6EE7] hover:bg-[#1C6EE7]/20'
                }`}
            >
                <div className="flex items-center gap-3">
                    {permission === 'denied' ? (
                        <BellOff className="h-5 w-5" />
                    ) : (
                        <Bell className="h-5 w-5" />
                    )}
                    <div className="text-left">
                        <p className="font-medium">
                            {permission === 'denied' 
                                ? 'Notifications Blocked' 
                                : 'Enable Deadline Alerts'}
                        </p>
                        <p className="text-xs opacity-80">
                            {permission === 'denied'
                                ? 'Enable in browser settings'
                                : 'Get reminded 7, 3, and 1 day before deadlines'}
                        </p>
                    </div>
                </div>
                {permission !== 'denied' && (
                    <div className="bg-[#1C6EE7] text-white px-3 py-1.5 rounded-full text-sm font-medium">
                        Enable
                    </div>
                )}
            </button>
        );
    }

    // Permission granted - show settings
    const intervals = getReminderIntervals();

    return (
        <div className="space-y-3">
            {/* Main toggle */}
            <div 
                className={`p-4 rounded-2xl transition-all ${
                    prefs.enabled 
                        ? 'bg-emerald-50' 
                        : 'bg-slate-50'
                }`}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {prefs.enabled ? (
                            <BellRing className="h-5 w-5 text-emerald-600" />
                        ) : (
                            <BellOff className="h-5 w-5 text-slate-400" />
                        )}
                        <div>
                            <p className={`font-medium ${prefs.enabled ? 'text-emerald-700' : 'text-slate-600'}`}>
                                Deadline Reminders
                            </p>
                            <p className="text-xs text-slate-500">
                                {prefs.enabled ? 'Active' : 'Paused'}
                            </p>
                        </div>
                    </div>
                    <Switch
                        checked={prefs.enabled}
                        onCheckedChange={handleToggleEnabled}
                        data-testid="notifications-enabled-toggle"
                    />
                </div>

                {/* Expand/collapse settings */}
                {prefs.enabled && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="w-full mt-3 pt-3 border-t border-emerald-200 flex items-center justify-center gap-1 text-sm text-emerald-600 hover:text-emerald-700"
                        data-testid="notification-settings-toggle"
                    >
                        <Settings className="h-4 w-4" />
                        <span>Customize</span>
                        {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                        ) : (
                            <ChevronDown className="h-4 w-4" />
                        )}
                    </button>
                )}
            </div>

            {/* Expanded settings */}
            {prefs.enabled && isExpanded && (
                <div className="p-4 bg-slate-50 rounded-2xl space-y-4">
                    <div>
                        <Label className="text-sm font-medium text-slate-700 mb-3 block">
                            Remind me before deadlines:
                        </Label>
                        <div className="space-y-3">
                            {intervals.map(({ days, label }) => (
                                <div 
                                    key={days} 
                                    className="flex items-center justify-between"
                                >
                                    <span className="text-sm text-slate-600">{label}</span>
                                    <Switch
                                        checked={prefs.intervals[days]}
                                        onCheckedChange={(checked) => handleIntervalToggle(days, checked)}
                                        data-testid={`interval-${days}-toggle`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="pt-3 border-t border-slate-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label className="text-sm font-medium text-slate-700">
                                    Critical deadlines only
                                </Label>
                                <p className="text-xs text-slate-500">
                                    Only notify for statute limitations
                                </p>
                            </div>
                            <Switch
                                checked={prefs.criticalOnly}
                                onCheckedChange={handleCriticalOnlyToggle}
                                data-testid="critical-only-toggle"
                            />
                        </div>
                    </div>

                    {/* Test notification button */}
                    <button
                        onClick={handleTestNotification}
                        disabled={isSendingTest}
                        className="w-full mt-2 flex items-center justify-center gap-2 py-2 text-sm text-[#1C6EE7] hover:bg-[#1C6EE7]/10 rounded-xl transition-colors"
                        data-testid="test-notification-btn"
                    >
                        <Send className="h-4 w-4" />
                        {isSendingTest ? 'Sending...' : 'Send Test Notification'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default NotificationToggle;
